import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ShieldCheck, Facebook, Instagram, Linkedin, Twitter, MessageCircle } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";
import { parseKeyValueMap } from "@/lib/pageContent";
import { pickLanguage } from "@/lib/publicLanguage";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { usePublicSettings } from "@/hooks/usePublicSiteData";

export default function Footer() {
  const { language } = useSiteLanguage();
  const { data: settings } = usePublicSettings();
  const ui = getPublicUiCopy(language, "footer");

  const facebookUrl = settings?.facebook_url || "https://www.facebook.com/rsintl23";
  const instagramUrl = settings?.instagram_url || "https://www.instagram.com/rsintl23/";
  const linkedinUrl = settings?.linkedin_url || "https://www.linkedin.com/company/rsintl23";
  const twitterUrl = settings?.twitter_url || "";
  const whatsappUrl = settings?.whatsapp_url || "https://wa.me/8801722772003";
  const footerLabels = parseKeyValueMap(settings?.footer_labels || "");
  const localizedFooter = {
    description: pickLanguage(language, {
      en:
        settings?.footer_description ||
        "Importer, seller and marketer of all types of products. General order collector and supplier serving B2B partners across the world.",
      "zh-CN":
        "面向全球B2B买家的进口、供应与市场协调合作伙伴，专注于清晰沟通、可信流程与长期贸易关系。",
      bn:
        "বিশ্বব্যাপী B2B ক্রেতাদের জন্য আমদানি, সরবরাহ ও বাজার-সমন্বয় সেবা প্রদানকারী অংশীদার; পরিষ্কার যোগাযোগ, নির্ভরযোগ্য প্রক্রিয়া এবং দীর্ঘমেয়াদি বাণিজ্যিক সম্পর্কের ওপর জোর দেয়।",
    }),
    verified: pickLanguage(language, {
      en: footerLabels.verified || "Verified Trader",
      "zh-CN": "可信贸易伙伴",
      bn: "যাচাইকৃত বাণিজ্য অংশীদার",
    }),
    explore: pickLanguage(language, {
      en: footerLabels.explore || "Explore",
      "zh-CN": "快速导航",
      bn: "দ্রুত নেভিগেশন",
    }),
    products: pickLanguage(language, {
      en: footerLabels.products || "Product Catalog",
      "zh-CN": "产品目录",
      bn: "পণ্যের ক্যাটালগ",
    }),
    about: pickLanguage(language, {
      en: footerLabels.about || "Corporate Profile",
      "zh-CN": "企业简介",
      bn: "কোম্পানি প্রোফাইল",
    }),
    trust: pickLanguage(language, {
      en: footerLabels.trust || "Trust Center",
      "zh-CN": "信任中心",
      bn: "ট্রাস্ট সেন্টার",
    }),
    track: pickLanguage(language, {
      en: footerLabels.track || "Track Order",
      "zh-CN": "订单追踪",
      bn: "অর্ডার ট্র্যাক",
    }),
    contactLink: pickLanguage(language, {
      en: footerLabels.contact_link || "Request a Quote",
      "zh-CN": "提交询盘",
      bn: "কোটেশন অনুরোধ করুন",
    }),
    faq: pickLanguage(language, {
      en: footerLabels.faq || "FAQ",
      "zh-CN": "常见问题",
      bn: "প্রশ্নোত্তর",
    }),
    terms: pickLanguage(language, {
      en: footerLabels.terms || "Terms & Conditions",
      "zh-CN": "条款与条件",
      bn: "শর্তাবলি",
    }),
    privacy: pickLanguage(language, {
      en: footerLabels.privacy || "Privacy Policy",
      "zh-CN": "隐私政策",
      bn: "গোপনীয়তা নীতি",
    }),
    contact: pickLanguage(language, {
      en: footerLabels.contact || "Get In Touch",
      "zh-CN": "联系我们",
      bn: "যোগাযোগ করুন",
    }),
    proprietor: pickLanguage(language, {
      en: "Proprietor",
      "zh-CN": "负责人",
      bn: "স্বত্বাধিকারী",
    }),
    copyright: pickLanguage(language, {
      en: "All rights reserved.",
      "zh-CN": "版权所有。",
      bn: "সর্বস্বত্ব সংরক্ষিত।",
    }),
    bottomNote: pickLanguage(language, {
      en: settings?.footer_bottom_note || "Built for global B2B trade",
      "zh-CN": "为全球B2B贸易打造",
      bn: "বিশ্বব্যাপী B2B বাণিজ্যের জন্য নির্মিত",
    }),
  };

  return (
    <footer className="bg-[var(--rsi-navy)] text-white" data-testid="public-footer">
      <div className="container-rsi py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-12 w-12 rounded-xl overflow-hidden bg-white border border-white/15 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.8)] p-1.5">
                <img src={LOGO_URL} alt="RS International" className="h-full w-full object-contain" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-extrabold text-lg">{settings?.company_name || "RS INTERNATIONAL"}</div>
              </div>
            </div>
            <p className="text-sm text-white/70 max-w-sm leading-relaxed">
              {localizedFooter.description}
            </p>

            <div className="mt-6 flex items-center gap-2">
              <span className="badge-verified">
                <ShieldCheck className="h-3.5 w-3.5" /> {localizedFooter.verified}
              </span>
            </div>

            {/* Social */}
            <div className="mt-6 flex items-center gap-2">
              {facebookUrl && (
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-facebook"
                  aria-label={ui.facebook}
                  className="h-10 w-10 rounded-lg bg-white/10 hover:bg-white/15 grid place-items-center transition-colors"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {instagramUrl && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-instagram"
                  aria-label={ui.instagram}
                  className="h-10 w-10 rounded-lg bg-white/10 hover:bg-white/15 grid place-items-center transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {linkedinUrl && (
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-linkedin"
                  aria-label={ui.linkedIn}
                  className="h-10 w-10 rounded-lg bg-white/10 hover:bg-white/15 grid place-items-center transition-colors"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
              {twitterUrl && (
                <a
                  href={twitterUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={ui.twitter}
                  className="h-10 w-10 rounded-lg bg-white/10 hover:bg-white/15 grid place-items-center transition-colors"
                >
                  <Twitter className="h-4 w-4" />
                </a>
              )}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-whatsapp"
                  aria-label={ui.whatsapp}
                  className="h-10 w-10 rounded-lg bg-white/10 hover:bg-white/15 grid place-items-center transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-display font-extrabold text-sm uppercase tracking-wider text-white/90 mb-4">
              {localizedFooter.explore}
            </h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link to="/products" className="hover:text-white" data-testid="footer-products">{localizedFooter.products}</Link></li>
              <li><Link to="/about" className="hover:text-white" data-testid="footer-about">{localizedFooter.about}</Link></li>
              <li><Link to="/trust-center" className="hover:text-white" data-testid="footer-trust">{localizedFooter.trust}</Link></li>
              <li><Link to="/track" className="hover:text-white" data-testid="footer-track">{localizedFooter.track}</Link></li>
              <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">{localizedFooter.contactLink}</Link></li>
              <li><Link to="/faq" className="hover:text-white" data-testid="footer-faq">{localizedFooter.faq}</Link></li>
              <li><Link to="/terms-and-conditions" className="hover:text-white" data-testid="footer-terms">{localizedFooter.terms}</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy">{localizedFooter.privacy}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-5">
            <h4 className="font-display font-extrabold text-sm uppercase tracking-wider text-white/90 mb-4">
              {localizedFooter.contact}
            </h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-white/60" />
                <span>{settings?.address || "Natun Bazar, Barishal Sadar, Barishal, Bangladesh"}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-white/60" />
                <a href={`tel:${settings?.phone_primary || "+8801722772003"}`} className="hover:text-white">
                  {settings?.phone_primary || "+8801722-772003"}
                </a>
                <span className="text-white/40">·</span>
                <a href={`tel:${settings?.phone_secondary || "+8613036168820"}`} className="hover:text-white">
                  {settings?.phone_secondary || "+8613036168820"}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-white/60" />
                <a href={`mailto:${settings?.email || "info@rsintl23.com"}`} className="hover:text-white">
                  {settings?.email || "info@rsintl23.com"}
                </a>
              </li>
              <li className="text-xs text-white/50 pt-2">
                {localizedFooter.proprietor}: {settings?.proprietor || "Engr. Dibakar Howlader"}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} RS International. {localizedFooter.copyright}</div>
          <div className="flex items-center gap-4">
            <span>{localizedFooter.bottomNote}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
